import { z } from 'zod';
import { ORDER_STATUSES } from '../domain/order-state.js';
export const addOrderCommentSchema = z
    .object({
    text: z
        .string()
        .trim()
        .min(1)
        .max(500)
        .regex(/^[\p{L}\p{M}\p{N}\p{P}\p{Zs}\r\n]+$/u),
})
    .strict();
import { wordCount } from '../domain/text.js';
export const createOrderSchema = z.object({
    partnerId: z.string().uuid(),
    // Calendar date, not an instant: preserve the partner's day in every timezone.
    requestedWorkDate: z.string().date().optional(),
    notes: z.string().trim().max(2000).optional(),
    partnerProjectRef: z.string().trim().max(120).optional(),
    partnerProjectName: z.string().trim().max(200).optional(),
});
export const orderLineInputSchema = z.object({
    productSkuId: z.string().uuid(),
    qty: z.number().positive().max(1_000_000),
    unitPriceCents: z.number().int().min(0).optional(),
    comment: z.string().trim().max(500).optional(),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const replaceOrderLinesSchema = z.object({
    lines: z.array(orderLineInputSchema).max(200),
});
export const replaceOrderPalletsSchema = z.object({
    palletIds: z.array(z.string().uuid()).max(50),
});
const packedOrderLineSchema = z.object({
    orderLineId: z.string().uuid(),
    qty: z.number().positive().max(1_000_000),
});
export const packOrderPalletsSchema = z
    .object({
    pallets: z
        .array(z
        .object({
        lines: z.array(packedOrderLineSchema).min(1).max(200),
    })
        .strict()
        .superRefine((pallet, ctx) => {
        const ids = pallet.lines.map((line) => line.orderLineId);
        if (new Set(ids).size !== ids.length) {
            ctx.addIssue({ code: 'custom', message: 'duplicate_order_line' });
        }
    }))
        .min(1)
        .max(50),
})
    .strict();
// Explicit draft checkpoint (WMS-52). Draft editors already persist each
// section independently; this action confirms the whole draft without submit.
export const saveOrderDraftSchema = z.object({}).strict();
// Warehouse-only correction for a draft created under the wrong partner.
// Partner-owned lines/pallets cannot cross the catalog boundary and are
// intentionally cleared by the API; project metadata, notes and delivery
// details remain on the order.
export const transferOrderPartnerSchema = z
    .object({
    partnerId: z.string().uuid(),
})
    .strict();
// Amend an already-accepted order (QA #8 / WMS-9). Every change needs a
// justification comment of at least two words; it's stored and shown on the
// order timeline.
export const amendOrderSchema = z.object({
    lines: z.array(orderLineInputSchema).max(200),
    comment: z
        .string()
        .trim()
        .max(1000)
        .refine((s) => wordCount(s) >= 2, { message: 'comment_too_short' }),
});
export const deliveryGroupLineInputSchema = z.object({
    /** Reference to an OrderLine.id from this same order. */
    orderLineId: z.string().uuid(),
    qty: z.number().positive(),
});
export const deliveryGroupInputSchema = z.object({
    // Delivery details are intentionally completable later (WMS-35/56). A group
    // may be saved with a blank address while the order is being prepared.
    address: z.string().trim().max(1000),
    city: z.string().trim().max(120).optional(),
    street: z.string().trim().max(200).optional(),
    houseNumber: z.string().trim().max(40).optional(),
    unitNumber: z.string().trim().max(40).optional(),
    addressComment: z.string().trim().max(500).optional(),
    /** Legacy primary-phone input retained for older clients. */
    phone: z.string().trim().max(60).optional(),
    phones: z.array(z.string().trim().min(1).max(60)).max(10).optional(),
    preferredDate: z.coerce.date().optional(),
    pickupAtWarehouse: z.boolean().default(false),
    deliveryFeeCents: z.number().int().min(0).default(0),
    sortOrder: z.number().int().min(0).max(999).default(0),
    // Pallet-only orders have no OrderLine rows, so their delivery group carries
    // address/contact/date information without product-line allocations.
    lines: z.array(deliveryGroupLineInputSchema).max(200),
});
export const replaceDeliveryGroupsSchema = z.object({
    groups: z.array(deliveryGroupInputSchema).max(50),
});
export const transitionOrderSchema = z.object({
    toStatus: z.enum(ORDER_STATUSES),
    reason: z.string().trim().max(2000).optional(),
});
export const updateDeliveryGroupStatusSchema = z.object({
    status: z.enum(['PENDING', 'OUT_FOR_DELIVERY', 'DELIVERED', 'PICKED_UP', 'FAILED']),
    fulfilledDate: z.coerce.date().optional(),
    workDate: z.coerce.date().optional(),
});
// Estimated work-completion date/time (WMS-31). `null` clears it. Set on the
// order and editable until the order reaches READY_FOR_SHIPMENT.
export const setWorkCompletionSchema = z.union([
    z.object({ at: z.coerce.date().nullable() }).strict(),
    z.object({ acceptRequested: z.literal(true) }).strict(),
]);
// Order notes (Pastabos) — correctable/fillable until the order is assembled
// (WMS-36). Empty string clears them.
export const updateOrderNotesSchema = z.object({
    notes: z.string().trim().max(2000),
});
// Partner-facing project metadata. The reference is optional on updates so a
// partner can change only the project name after submission (WMS-92). The
// service retains the stricter reference permission and freezes both fields
// when assembly starts.
export const updateOrderProjectSchema = z
    .object({
    partnerProjectRef: z.string().trim().max(120).nullable().optional(),
    partnerProjectName: z.string().trim().max(200).nullable(),
})
    .strict();
// Manual resolution for an invoice push whose outcome could not be established
// safely. Only accountants/admins can reach the endpoint (`invoice.push`).
export const reconcileOrderInvoiceSchema = z.discriminatedUnion('outcome', [
    z.object({
        outcome: z.literal('FOUND'),
        invoiceRef: z.string().trim().min(1).max(200),
        invoiceNumber: z.string().trim().min(1).max(200).optional(),
        invoiceUrl: z.string().url().max(2000).optional(),
        reason: z.string().trim().min(5).max(2000),
    }),
    z.object({
        outcome: z.literal('NOT_CREATED'),
        reason: z.string().trim().min(5).max(2000),
    }),
]);
export const updateOrderPricingSchema = z.object({
    assemblyFeeCents: z.number().int().min(0).optional(),
    partsPassThroughCents: z.number().int().min(0).optional(),
    notes: z.string().trim().max(2000).optional(),
    partnerProjectRef: z.string().trim().max(120).nullable().optional(),
    partnerProjectName: z.string().trim().max(200).nullable().optional(),
});
//# sourceMappingURL=order.js.map